// Calendar & Reminders (Apple, native app only): this device's choices.
// The server never sees any of it (contract §4). Stored user-scoped
// (cleared at sign-out, and stamped with the device's data owner — see
// readDataOwnerUid — so another account's choices never load) under
// APPLE_SETTINGS_KEY.
//
// This module holds preferences only: it never touches EventKit, so any
// surface may read it (the meeting prompt, the Connections screen).

import { useSyncExternalStore } from 'react';
import { loadOwnedFromStorageAsync, readDataOwnerUid, saveOwnedToStorageAsync } from '../../services/storage';

export const APPLE_SETTINGS_KEY = 'rhemn:appleCalendarReminders:v1';

export interface AppleSettings {
  /** The user connected Calendar & Reminders in Rhemn on this device. */
  enabled: boolean;
  /** null = not chosen yet → the default calendar only (§5.3). */
  calendarIds: string[] | null;
  /** null = not chosen yet → the default Reminders list only (§5.3). */
  reminderListIds: string[] | null;
  writePrayerTimes: boolean;
  /** YYYY-MM-DD, the last day written ("Added through {date}"). */
  prayerTimesWrittenThrough: string | null;
  meetingPrompts: boolean;
}

export const DEFAULT_APPLE_SETTINGS: AppleSettings = {
  enabled: false,
  calendarIds: null,
  reminderListIds: null,
  writePrayerTimes: false,
  prayerTimesWrittenThrough: null,
  meetingPrompts: true,
};

let settings: AppleSettings = DEFAULT_APPLE_SETTINGS;
let loaded = false;
let loading: Promise<void> | null = null;
// Bumped by update/reset: a storage read that started earlier is stale and
// must not overwrite what the user (or the sign-out reset) set since.
let generation = 0;
const listeners = new Set<() => void>();

const emit = (): void => listeners.forEach((l) => l());

const ids = (v: unknown): string[] | null =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : null;

const sanitize = (raw: unknown): AppleSettings => {
  if (typeof raw !== 'object' || raw === null) return DEFAULT_APPLE_SETTINGS;
  const r = raw as Partial<Record<keyof AppleSettings, unknown>>;
  return {
    enabled: r.enabled === true,
    calendarIds: ids(r.calendarIds),
    reminderListIds: ids(r.reminderListIds),
    writePrayerTimes: r.writePrayerTimes === true,
    prayerTimesWrittenThrough: typeof r.prayerTimesWrittenThrough === 'string' ? r.prayerTimesWrittenThrough : null,
    meetingPrompts: r.meetingPrompts !== false,
  };
};

/** Load once per page; resolves immediately afterwards. */
export const loadAppleSettings = (): Promise<void> => {
  if (loaded) return Promise.resolve();
  if (!loading) {
    const myGen = generation;
    loading = loadOwnedFromStorageAsync(APPLE_SETTINGS_KEY, readDataOwnerUid()).then((raw) => {
      if (myGen !== generation) return;
      settings = sanitize(raw);
      loaded = true;
      emit();
    });
  }
  return loading;
};

export const getAppleSettings = (): AppleSettings => settings;

export const updateAppleSettings = (patch: Partial<AppleSettings>): AppleSettings => {
  generation += 1;
  settings = { ...settings, ...patch };
  loaded = true;
  const owner = readDataOwnerUid();
  // No owner yet = nobody signed in on this device: keep it in memory only.
  if (owner) void saveOwnedToStorageAsync(APPLE_SETTINGS_KEY, owner, settings);
  emit();
  return settings;
};

/** Sign-out cleared storage: forget the in-memory copy too. */
export const resetAppleSettings = (): void => {
  generation += 1;
  settings = DEFAULT_APPLE_SETTINGS;
  loaded = false;
  loading = null;
  emit();
};

// ── Pro gate ────────────────────────────────────────────────────────────────
// The bridge does not gate Pro, so the app does: the native shell reports
// the entitlement here, and every Apple reader checks appleInUse().

let proAccess = false;

export const setAppleProAccess = (isPro: boolean): void => {
  if (proAccess === isPro) return;
  proAccess = isPro;
  emit();
};

/** Connected in Rhemn on this device AND Pro. */
export const appleInUse = (): boolean => proAccess && settings.enabled;

export const subscribeAppleSettings = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

export const useAppleSettings = (): AppleSettings => {
  if (!loaded) void loadAppleSettings();
  return useSyncExternalStore(subscribeAppleSettings, getAppleSettings, getAppleSettings);
};
